/**
 * compartmentPaneItems — data builders for CompartmentsPane.
 *
 * Covers three concerns:
 * 1. getCompartmentName     — resolve display name from catalog compartments_types
 * 2. buildCompartmentTileSettings — visibility flags for the expanded tile detail rows
 * 3. buildAdditionalAreasSection  — readonly rows: inner walls + additional_unit_areas
 *
 * UI-free, JSX-free, fully testable.
 */

import { formatSquareMeters } from '../../../shared/utils/productFormatters.js'

// ── getCompartmentName ────────────────────────────────────────────────────────

/**
 * Resolves the human-readable name for a compartment — long and short variants.
 *
 * Primary source: i18n keys `compartments.{nameCad}` and
 * `compartments.{nameCad}_short`.
 * Fallback chain: catalog `compartments_types[nameCad].name_pl` → nameCad → ''
 *
 * - `name`      — full name, suitable for tooltip / title attribute
 * - `nameShort` — abbreviated name for tile labels; falls back to full `name`
 *                 when no dedicated short key is defined in i18n
 *
 * @param {string}      nameCad          — compartment.name_cad value
 * @param {object|null} compartmentTypes — raw compartments.json from useCatalog().compartments
 * @param {function}    t                — i18n function, must support t(key, fallback)
 * @returns {{ name: string, nameShort: string }}
 */
export function getCompartmentName(nameCad, compartmentTypes, t) {
  const catalogFallback = compartmentTypes?.compartments_types?.[nameCad]?.name_pl ?? nameCad ?? ''
  const name      = t(`compartments.${nameCad}`,       catalogFallback)
  const nameShort = t(`compartments.${nameCad}_short`, name)
  return { name, nameShort }
}

// ── buildCompartmentTileSettings ─────────────────────────────────────────────

/**
 * Builds the settings object consumed by buildCompartmentItems() for the expanded
 * tile detail rows. Keys are intentionally identical to those used in the
 * compartment popover settings (PlanRendererInfoMode) so both go through the
 * same buildCompartmentItems() registry.
 *
 * Reads from panelSettings.unit_sheet_compartments.tile_expansion_fields.
 * All fields default to true when the key is absent.
 *
 * @param {object} panelSettings
 * @param {object} capabilities — product capabilities (hasInsolationData, etc.)
 * @returns {object}
 */
export function buildCompartmentTileSettings(panelSettings, capabilities) {
  const ps = panelSettings?.unit_sheet_compartments
  const fields = ps?.tile_expansion_fields

  const tileExpansionEnabled = ps?.tile_expansion_enabled ?? true

  if (!tileExpansionEnabled) {
    return {
      tileExpansionEnabled: false,
      showArea:            false,
      showExposition:      false,
      showInsolationRange: false,
      showInsolationHours: false,
      showWindowsRatio:    false,
      showWindowsArea:     false,
      showZone:            false,
    }
  }

  return {
    tileExpansionEnabled: true,
    showArea:            fields?.area              ?? true,
    showExposition:      fields?.exposition        ?? true,
    showInsolationRange: (fields?.insolation_range ?? true) && (capabilities?.hasInsolationData ?? true),
    showInsolationHours: (fields?.insolation_hours ?? true) && (capabilities?.hasInsolationData ?? true),
    showWindowsRatio:    fields?.windows_ratio     ?? true,
    showWindowsArea:     fields?.windows_area      ?? true,
    showZone:            fields?.zone              ?? true,
  }
}

// ── buildAdditionalAreasSection ───────────────────────────────────────────────

/**
 * Builds the readonly "additional areas" rows rendered below the compartment grids.
 *
 * Covers two sources:
 * - inner_walls:           panelSettings.inner_walls + product.area_walls
 * - additional_unit_areas: match by label string between panelSettings.additional_unit_areas.areas[]
 *                          and product.additional_unit_areas[]
 *
 * @param {object}   product
 * @param {object}   panelSettings
 * @param {function} t — i18n function
 * @returns {{ innerWalls: DataItem|null, additionalAreas: DataItem[] }}
 *   where DataItem = { label: string, value: string, tooltip: {text:string, text_html:string}|null }
 */
export function buildAdditionalAreasSection(product, panelSettings, t) {
  // ── Inner walls ──────────────────────────────────────────────────────────
  let innerWalls = null
  const iwSettings = panelSettings?.inner_walls
  if (iwSettings?.is_visible === true && product?.area_walls != null) {
    innerWalls = {
      label:   iwSettings.label || t('product.inner_walls'),
      value:   formatSquareMeters(product.area_walls),
      tooltip: extractTooltip(iwSettings.tooltip),
    }
  }

  // ── Additional unit areas ────────────────────────────────────────────────
  const areasDefs  = panelSettings?.additional_unit_areas?.areas ?? []
  const productAreas = product?.additional_unit_areas ?? []

  const additionalAreas = []
  for (const def of areasDefs) {
    if (def.is_visible !== true) continue
    const match = productAreas.find((a) => a.label === def.label)
    if (match == null) continue
    additionalAreas.push({
      label:   def.label,
      value:   formatSquareMeters(match.value),
      tooltip: extractTooltip(def.tooltip),
    })
  }

  return { innerWalls, additionalAreas }
}

// ── helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns a tooltip object when at least one text field is non-empty, otherwise null.
 * Consumers treat null as "no tooltip icon".
 *
 * @param {{ text?: string, text_html?: string }|null|undefined} tooltipObj
 * @returns {{ text: string, text_html: string }|null}
 */
function extractTooltip(tooltipObj) {
  if (!tooltipObj) return null
  const { text = '', text_html = '' } = tooltipObj
  return (text || text_html) ? { text, text_html } : null
}
